import React, { useState, useEffect, useRef, useMemo } from 'react';
import './inputSelect.scss';
import { createPortal } from 'react-dom'; 
import { batchColorFormatTag, formatTag } from '../../utils/constant';
import { Tooltip } from 'react-tooltip';

const InputSelect = ({
	label,
	onFocusCb = () => {},
	onBlurCb = () => {},
	onKeyDownCb = null,
	onSelect,
	options = [],
	placeholder,
	onAddNew,
	onChange,
	returnInputValue,
	required,
	value,
	disabled,
	autoFocus,
	infoIcon,
	name,
	propInputRef,
	onClick,
	error,
	isLocalValidationError,
	onfucusCapture,
	className,
	moreInfobtn,
	tolipIcon = null,
	dropdownType = null,
	dataTestId = '',
	onClickDropdownIcon = () => null,
	leftIcons = [],
}) => {
	const [inputValue, setInputValue] = useState('');
	const [isDropdownVisible, setIsDropdownVisible] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(-1);
	const dropdownRef = useRef(null);
	const selectedItemRef = useRef(null);
	const inputRef = useRef(null);

	const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });

	const updateCoords = () => {
		if (inputRef.current) {
			const rect = inputRef.current.getBoundingClientRect();
			setCoords({
				top: rect.bottom + window.scrollY,
				left: rect.left + window.scrollX,
				width: rect.width,
			});
		}
	};

	useEffect(() => {
		if (isDropdownVisible) {
			updateCoords();
			window.addEventListener('scroll', updateCoords, true);
			window.addEventListener('resize', updateCoords);
		}
		return () => {
			window.removeEventListener('scroll', updateCoords, true);
			window.removeEventListener('resize', updateCoords);
		};
	}, [isDropdownVisible]);

	const handleDropDown = () => {
		setIsDropdownVisible(true);
	};

	useEffect(() => {
		function changeInputValue() {
			setInputValue(value ?? '');
		}
		changeInputValue();
	}, [value]);

	function getMonthAndYear(dateStr) {
		const date = new Date(dateStr);
		const year = date.getFullYear() % 100;
		const month = date.getMonth() + 1;
		return `${month}/${year}`;
	}

	useEffect(() => {
		if (!returnInputValue || !value) {
			const foundOption = options?.find((option) => option?.key === value);
			if (foundOption) {
				setInputValue(foundOption.value);
			}
		}
	}, [returnInputValue, value, options]);

	// Deduping with `acc.find` inside a reduce is O(n²), and the old
	// JSON.stringify equality check serialized the entire option list on every
	// pass. On a country/state dropdown re-rendering per keystroke that was a
	// measurable stall — a Map keyed by `key` gives identical output in O(n).
	const filteredOptions = useMemo(() => {
		const uniqueOptions = Array.from(
			new Map((options || []).map((option) => [option?.key, option])).values(),
		);

		if (!inputValue) return uniqueOptions;

		const needle = inputValue.toString().toLowerCase();

		return uniqueOptions.filter(
			(option) =>
				option?.value?.toString().toLowerCase().includes(needle) ||
				(option?.short_name !== undefined &&
					option?.short_name?.toString().toLowerCase().includes(needle)),
		);
	}, [inputValue, options]);

	// Keyboard highlight resets whenever the visible option set changes.
	useEffect(() => {
		setSelectedIndex(-1);
	}, [filteredOptions]);

	useEffect(() => {
		const onReady = () => {
			if (autoFocus && inputRef.current) {
				inputRef.current.focus();
				handleDropDown();
			}
		};

		if (document.readyState === 'complete') {
			onReady();
		} else {
			window.addEventListener('load', onReady);
			return () => window.removeEventListener('load', onReady);
		}
	}, [autoFocus]);

	useEffect(() => {
		if (selectedItemRef.current && dropdownRef.current) {
			const dropdown = dropdownRef.current;
			const selectedItem = selectedItemRef.current;
			const dropdownRect = dropdown.getBoundingClientRect();
			const selectedItemRect = selectedItem.getBoundingClientRect();

			if (selectedItemRect.bottom > dropdownRect.bottom) {
				dropdown.scrollTop += selectedItemRect.bottom - dropdownRect.bottom;
			} else if (selectedItemRect.top < dropdownRect.top) {
				dropdown.scrollTop -= dropdownRect.top - selectedItemRect.top;
			}
		}
	}, [selectedIndex]);

	const handleInputChange = (e) => {
		setInputValue(e.target.value);
		setIsDropdownVisible(true);
		if (onChange) {
			onChange(e.target.value);
			error = null;
		}
	};

	const handleOptionClick = (option) => {
		setInputValue(option && option?.value ? option?.value : '');
		setIsDropdownVisible(false);
		if (onSelect && option !== undefined) {
			onSelect(option);
		} else {
			onChange(
				returnInputValue
					? option && option.value
						? option.value
						: ''
					: option && option.key
						? option.key
						: '',
			);
		}
	};

	const handleBlur = () => {
		setTimeout(() => {
			onBlurCb();
			setIsDropdownVisible(false);
		}, 200);
	};

	const handleKeyDown = (e) => {
		if (isDropdownVisible) {
			if (e.key === 'ArrowDown') {
				e.preventDefault();
				setSelectedIndex((prevIndex) => (prevIndex < filteredOptions.length ? prevIndex + 1 : 0));
			} else if (e.key === 'ArrowUp') {
				e.preventDefault();
				setSelectedIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : filteredOptions.length));
			} else if (e.key === 'Enter') {
				e.preventDefault();
				if (selectedIndex >= 0 && selectedIndex < filteredOptions.length) {
					handleOptionClick(filteredOptions[selectedIndex]);
				} else if (selectedIndex === filteredOptions.length) {
					handleAddNew();
				}
			} else if (e.key === 'Tab') {
				if (filteredOptions[selectedIndex]) {
					handleOptionClick(filteredOptions[selectedIndex]);
				} else {
					var options = filteredOptions.filter((item) => item.value === inputValue);
					if (options.length > 1) {
						options = options.filter((item) => item.key === value);
					}
					if (options.length === 1) {
						handleOptionClick(options[0]);
					} else {
						handleOptionClick();
					}
				}
			}
		}
		if (e.key === 'Tab' && e.shiftKey && onKeyDownCb !== null) {
			onKeyDownCb(e);
		}
	};

	const handleAddNew = () => {
		if (onAddNew) {
			onAddNew(inputValue);
		}
		setIsDropdownVisible(false);
	};

	useEffect(() => {
		return () => {
			setIsDropdownVisible(false);
		};
	}, []);

	const handleDropdownOpen = () => {
		setIsDropdownVisible(true);

		setTimeout(() => {
			const modalBody = document.querySelector('.advance-filter-body');

			if (!modalBody || !inputRef.current) return;

			const inputRect = inputRef.current.getBoundingClientRect();
			const modalRect = modalBody.getBoundingClientRect();

			const remainingSpace = modalRect.bottom - inputRect.bottom;

			if (remainingSpace < 200) {
				modalBody.scrollBy({
					top: 200 - remainingSpace,
					behavior: 'smooth',
				});
			}
		}, 50);
	};

	return (
		<div
			className={`input-select ${className}`}
			style={{ position: 'relative', display: 'inline-block', width: '100%' }}
		>
			<div
				className="mb-2"
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'flex-start',
				}}
			>
				{label && <label className="form-label mb-0">{label}</label>}
				{required && <span style={{ color: 'red', marginLeft: '.2rem' }}>*</span>}

				{tolipIcon && (
					<div className="px-2">
						<img
							src={tolipIcon.icon}
							alt="info"
							width={15}
							height={15}
							data-tooltip-id={`tooltip-${tolipIcon.name}`}
							data-tooltip-content={tolipIcon.title}
							className="cursor-pointer"
						/>

						<Tooltip id={`tooltip-${tolipIcon.name}`} />
					</div>
				)}
				{moreInfobtn && (
					<span style={{ marginLeft: '140px', fontWeight: '600', fontSize: '14px' }}>
						{moreInfobtn}
					</span>
				)}
			</div>

			<div
				style={{
					position: 'relative',
					display: 'flex',
					alignItems: 'center',
					border: error || isLocalValidationError ? '1px solid #f71326' : '1px solid #ccc',
					borderRadius: '6px',
				}}
				className="mb-3"
			>
				{/* Left side icons (Images) */}

				{leftIcons.length > 0 && (
					<div
						className="left-icons"
						style={{
							position: 'absolute',
							right: '35px',
							display: 'flex',
							zIndex: '1',
							pointerEvents: 'auto',
						}}
					>
						{leftIcons.map((icon, index) => (
							<img
								key={index}
								src={icon.src}
								alt={icon.alt}
								style={{
									width: '18px',
									height: '18px',
									marginRight: index === leftIcons.length - 1 ? '0' : '8px',
									cursor: 'pointer',
									zIndex: '1',
								}}
								onClick={icon.onClick}
							/>
						))}
					</div>
				)}

				<input
					ref={(node) => {
						inputRef.current = node;
						if (propInputRef) {
							if (typeof propInputRef === 'function') propInputRef(node);
							else propInputRef.current = node;
						}
					}}
					type="text"
					value={inputValue}
					onChange={handleInputChange}
					data-testid={dataTestId}
					onBlur={() => {
						handleBlur();
					}}
					onFocus={() => {
						onFocusCb();
						setIsDropdownVisible(true);
						handleDropdownOpen();
					}}
					onKeyDown={(e) => handleKeyDown(e)}
					placeholder={placeholder}
					style={{
						width: '100%',
						padding: '8px',
						boxSizing: 'border-box',
						borderRadius: '6px',
						paddingRight: '30px',
						background: '#FCFCFC',
						border: '0px solid #ccc',
						height: '34px',
					}}
					disabled={disabled}
					autoFocus={autoFocus}
					name={name}
					className={`inputBG`}
					onClick={handleDropDown}
					onFocusCapture={onfucusCapture}
				/>
				<i
					className="fa fa-caret-down"
					style={{
						position: 'absolute',
						top: '50%',
						right: '10px',
						transform: 'translateY(-50%)',
						cursor: 'pointer',
					}}
					onClick={() => {
						setIsDropdownVisible((prevState) => !prevState);
						if (inputRef.current) {
							inputRef.current.focus();
						}
					}}
				/>
			</div>

			{error && (
				<div
					className="error-text"
					style={{ color: 'red', fontSize: '12px', marginBottom: '10px' }}
				>
					{error}
				</div>
			)}
			{isDropdownVisible && !disabled && createPortal(
				<span
					className={`d-block`}
					style={{
						position: 'absolute',
						top: `${coords.top}px`,
						left: `${coords.left}px`,
						width: `${coords.width}px`,
						zIndex: 99999,
					}}
				>
					<ul
						className="dropdown-list"
						ref={dropdownRef}
						style={{
							position: 'absolute',
							top: '0',
							left: '0',
							right: '0',
							border: '1px solid #ccc',
							borderBottom: onAddNew ? '0' : '',
							borderRadius: '8px',
							maxHeight: onAddNew ? '115px' : '150px',
							minHeight: onAddNew ? '115px' : 'auto',
							overflowY: 'auto',
							backgroundColor: '#FCFCFC',
							listStyleType: 'none',
							padding: '0',
							margin: '0',
							width: '100%',
							boxSizing: 'border-box',
							alignContent: 'flex-start',
							zIndex: 1000,
							borderBottomRightRadius: onAddNew ? '0' : '',
							borderBottomLeftRadius: onAddNew ? '0' : '',
						}}
					>
						{filteredOptions.length > 0 ? (
							filteredOptions.map((option, index) => {
								const showCategory =
									index === 0 || filteredOptions[index - 1]?.category !== option.category;

								return (
									<React.Fragment key={index}>
										{/* CATEGORY HEADER (NOT SELECTABLE) */}
										{showCategory && dropdownType === 'category-wise' && (
											<div
												style={{
													padding: '8px 12px',
													fontWeight: '600',
													fontSize: '15px',
													cursor: 'default',
												}}
											>
												{option?.category}
											</div>
										)}

										{/* SELECTABLE OPTION */}
										<li
											ref={index === selectedIndex ? selectedItemRef : null}
											onClick={() => handleOptionClick(option)}
											onMouseEnter={() => setSelectedIndex(index)}
											style={{
												padding: `${dropdownType === 'category-wise' ? '8px 12px 8px 15px' : '8px 12px'}`,
												cursor: 'pointer',
												textAlign: 'left',
												listStyle: 'none',
												backgroundColor: selectedIndex === index ? '#bde4ff' : 'transparent',
											}}
											onMouseDown={(e) => e.preventDefault()}
										>
											{option.tag ? (
												<div className="d-flex justify-content-between">
													<span style={{ fontSize: '1.5rem' }}>{option.value}</span>

													<span
														style={{
															color: batchColorFormatTag(option.tag),
															fontSize: '1.5rem',
														}}
													>
														{formatTag(option.tag)}
													</span>
												</div>
											) : (
												option?.month || option.value
											)}

											{/* EXTRA INFO */}
											{option?.qty || option?.expiry_date || option?.mrp ? (
												<div
													style={{
														display: 'flex',
														justifyContent: 'space-between',
														fontSize: '10px',
														fontWeight: '400',
														marginTop: '4px',
													}}
												>
													<span>{`QTY: ${option?.qty}`}</span>
													<span>{`EXP: ${option?.expiry_date}`}</span>
													<span>{`MRP: ₹ ${option?.mrp}`}</span>
												</div>
											) : null}
										</li>
									</React.Fragment>
								);
							})
						) : (
							<li
								style={{
									padding: '8px',
									cursor: 'default',
									textAlign: 'start',
									listStyle: 'none',
								}}
							>
								No options found
							</li>
						)}
					</ul>
					{onAddNew && (
						<span
							className="addNew"
							style={{
								position: 'absolute',
								top: '115px',
								left: '0',
								right: '0',
								zIndex: 1001,
								display: 'block',
								backgroundColor: selectedIndex === filteredOptions.length ? '#bde4ff' : '#ffff',
								border: '1px solid #ccc',
								borderTop: '0',
								borderBottomRightRadius: '8px',
								borderBottomLeftRadius: '8px',
							}}
						>
							<li
								ref={selectedIndex === filteredOptions.length ? selectedItemRef : null}
								onClick={handleAddNew}
								style={{
									padding: '8px',
									cursor: 'pointer',
									textAlign: 'start',
									color: 'blue',
									listStyle: 'none',
								}}
								onMouseDown={(e) => e.preventDefault()}
							>
								Add New
							</li>
						</span>
					)}
				</span>,
				document.body,
			)}
		</div>
	);
};

export default InputSelect;
